import {APP_INITIALIZER, NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import {HeaderInterceptor} from "./interceptors/header.interceptor";
import {RouterOutlet} from "@angular/router";
import {SharedModule} from "./shared/shared.module";
import {DialogService, DynamicDialogModule} from "primeng/dynamicdialog";
import {CommonModule} from "@angular/common";
import {ConfirmDialogModule} from "primeng/confirmdialog";
import {ButtonModule} from "primeng/button";
import {TableModule} from "primeng/table";
import {MultiSelectModule} from "primeng/multiselect";
import {PaginatorModule} from "primeng/paginator";
import {SliderModule} from "primeng/slider";
import {ProgressBarModule} from "primeng/progressbar";
import {InputTextModule} from "primeng/inputtext";
import {RippleModule} from "primeng/ripple";
import {TooltipModule} from "primeng/tooltip";
import {DialogModule} from "primeng/dialog";
import {ReactiveFormsModule} from "@angular/forms";
import {PasswordModule} from "primeng/password";
import {RadioButtonModule} from "primeng/radiobutton";
import {NoauthroizedComponent} from "./components/noauthroized/noauthroized.component";
import {MaintenanceComponent} from "./components/maintenance/maintenance.component";
import {TableCustomerComponent} from "./components/maintenance/table-customer/table-customer.component";
import {AssignmentDialogComponent} from "./components/maintenance/assignment-dialog/assignment-dialog.component";
import {AutoCompleteModule} from "primeng/autocomplete";
import {SearchDialogComponent} from './components/maintenance/search-dialog/search-dialog.component';
import {ToggleSwitchModule} from "primeng/toggleswitch";
import {ToastModule} from "primeng/toast";
import {DrawerModule} from "primeng/drawer";
import {MenuModule} from "primeng/menu";
import {PopoverModule} from "primeng/popover";
import { environment } from '../environments/environment';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';

import {
  SecureCommInterceptor,
  KeyExchangeService,
  KeyStorageService,
  SECURE_COMM_CONFIG,
} from '@secure-comm/angular';

// Secure-Comm Gateway Configuration
const secureComm = {
  apiBaseUrl: environment.secureComm.apiBaseUrl, // Empty to use relative URLs - proxy will redirect to AKS gateway
  keyExchangeEndpoint: '/crypto/key-exchange',
  encryptAll: false, // Only encrypt URLs matching encryptPatterns (more explicit)
  encryptPatterns: [
    /\/gateway-secure-comm-fm\/.*\/api\/.*/,  // Only encrypt Gateway URLs with /api/ path
  ],
  excludePatterns: [
    // Exceptions to encryptPatterns (URLs that match encryptPatterns but should NOT be encrypted)
    /\/crypto\/key-exchange/,  // Key exchange must be unencrypted
    /\/crypto\/health/,         // Health checks
    /\/actuator\/.*/,           // Spring actuator endpoints
    /\/assets\/.*/,             // Static assets
    /\.json$/,                  // i18n translation files
  ],
  keyRotationHours: 24,
  storageType: 'session' as const,
  debug: true,
  maxTimestampSkew: 3600000, // 60 minutes in milliseconds - CRÍTICO para AKS time differences
};

@NgModule({ declarations: [
        AppComponent,
        NoauthroizedComponent,
        MaintenanceComponent,
        TableCustomerComponent,
        AssignmentDialogComponent,
        SearchDialogComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        SharedModule,
        CommonModule,
        ConfirmDialogModule,
        ButtonModule,
        TableModule,
        MultiSelectModule,
        PaginatorModule,
        SliderModule,
        ProgressBarModule,
        InputTextModule,
        RippleModule,
        TooltipModule,
        DialogModule,
        ReactiveFormsModule,
        PasswordModule,
        RadioButtonModule,
        DynamicDialogModule,
        ToastModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        RouterOutlet,
        AutoCompleteModule,
        ToggleSwitchModule,
        DrawerModule,
        MenuModule,
        PopoverModule], providers: [
        DialogService,
        // Secure-Comm Configuration
        {
            provide: SECURE_COMM_CONFIG,
            useValue: secureComm,
        },
        // Secure-Comm Services
        KeyStorageService,
        {
            provide: KeyExchangeService,
            useFactory: keyExchangeServiceFactory,
            deps: [HttpClient, KeyStorageService, SECURE_COMM_CONFIG],
        },
        // HTTP Interceptors
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useFactory: secureCommInterceptorFactory,
            deps: [KeyExchangeService, SECURE_COMM_CONFIG],
            multi: true,
        },
        // APP Initializer for Secure-Comm
        {
            provide: APP_INITIALIZER,
            useFactory: initializeSecureComm,
            deps: [KeyStorageService, KeyExchangeService],
            multi: true,
        },
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
            theme: {
                preset: Aura,
                options:{
                    darkModeSelector: 'none'
                }
            },
        }),
    ] })
export class AppModule {
}

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// Secure-Comm Configuration Factories
export function keyExchangeServiceFactory(
  http: HttpClient,
  storage: KeyStorageService,
  config: any
): KeyExchangeService {
  console.log(
    '🔧 [Factory] Initializing KeyExchangeService with injected config:',
    {
      hasApiBaseUrl: !!config.apiBaseUrl,
      keyExchangeEndpoint: config.keyExchangeEndpoint,
      encryptAll: config.encryptAll,
      maxTimestampSkew: config.maxTimestampSkew,
      debug: config.debug,
      fullConfig: config,
    }
  );
  const service = new KeyExchangeService(http, storage);
  service.initialize(config);
  console.log('🔧 [Factory] Service initialized');
  return service;
}

export function secureCommInterceptorFactory(
  keyExchange: KeyExchangeService,
  config: any
): SecureCommInterceptor {
  console.log(
    '🔧 [Factory] Initializing SecureCommInterceptor with injected config:',
    {
      hasApiBaseUrl: !!config.apiBaseUrl,
      encryptAll: config.encryptAll,
      maxTimestampSkew: config.maxTimestampSkew,
    }
  );
  const interceptor = new SecureCommInterceptor(keyExchange);
  interceptor.initialize(config);
  console.log('🔧 [Factory] Interceptor initialized');
  return interceptor;
}

export function initializeSecureComm(
  storage: KeyStorageService,
  keyExchange: KeyExchangeService
) {
  return async () => {
    await storage.initialize(secureComm.storageType);

    try {
      console.log('🔑 Attempting key exchange with AKS gateway...');
      await keyExchange.performKeyExchange();
      console.log('✅ Key exchange successful');
    } catch (error: any) {
      console.warn(
        '⚠️ Key exchange failed, but continuing anyway:',
        error.message
      );
    }
  };
}
